import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../api/client';

type VerificationStatus = 'PENDING' | 'AWAITING_ADMIN_REVIEW' | 'RESUBMISSION_REQUESTED' | 'APPROVED' | 'REJECTED';

interface VerificationRow {
  id: number;
  userId: number;
  email: string;
  companyName: string | null;
  status: VerificationStatus;
  documentType: string | null;
  documentUrl: string | null;
  hasDiditSession: boolean;
  submittedAt: string;
}

const STATUS_FILTERS = ['PENDING', 'AWAITING_ADMIN_REVIEW', 'RESUBMISSION_REQUESTED', 'APPROVED', 'REJECTED', 'ALL'] as const;

const statusColor: Record<string, string> = {
  PENDING: 'text-gold bg-gold/10',
  AWAITING_ADMIN_REVIEW: 'text-gold bg-gold/10',
  RESUBMISSION_REQUESTED: 'text-warning bg-warning/10',
  APPROVED: 'text-success bg-success/10',
  REJECTED: 'text-error bg-error/10',
};

const toAbsoluteUrl = (url: string) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

// A row is reviewable once Didit's automated first-pass (or a legacy manual
// submission) has produced something for a human to act on — never
// RESUBMISSION_REQUESTED (waiting on the business) or a terminal state.
const isReviewable = (status: VerificationStatus) => status === 'PENDING' || status === 'AWAITING_ADMIN_REVIEW';

export default function AdminBusinessVerifications() {
  const [rows, setRows] = useState<VerificationRow[]>([]);
  const [filter, setFilter] = useState<typeof STATUS_FILTERS[number]>('PENDING');
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState<number | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [resubmissionNotes, setResubmissionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [diditModalId, setDiditModalId] = useState<number | null>(null);

  const load = useCallback((status: typeof STATUS_FILTERS[number]) => {
    setLoading(true);
    api
      .get('/business-finance/admin/verifications', { params: status === 'ALL' ? {} : { status } })
      .then((res) => setRows(res.data.items ?? []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(filter);
  }, [filter, load]);

  const review = async (id: number, status: 'APPROVED' | 'REJECTED' | 'RESUBMISSION_REQUESTED') => {
    if (status === 'REJECTED' && !rejectionReason.trim()) {
      alert('A rejection reason is required');
      return;
    }
    if (status === 'RESUBMISSION_REQUESTED' && !resubmissionNotes.trim()) {
      alert('Resubmission notes are required');
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/business-finance/admin/verifications/${id}`, {
        status,
        rejectionReason: status === 'REJECTED' ? rejectionReason.trim() : undefined,
        resubmissionNotes: status === 'RESUBMISSION_REQUESTED' ? resubmissionNotes.trim() : undefined,
      });
      setActingId(null);
      setRejectionReason('');
      setResubmissionNotes('');
      load(filter);
    } catch (e) {
      alert('Could not review verification');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold text-textPrimary">Business Verifications</h1>
        <div className="flex gap-2 flex-wrap">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`text-sm px-3 py-1.5 rounded-lg border transition ${
                filter === s ? 'bg-gold/10 border-gold/40 text-gold' : 'bg-surface1 border-border text-textSecondary hover:border-gold/30'
              }`}
            >
              {s.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-surface1 rounded-xl border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-textTertiary">
            <tr>
              <th className="text-left py-3 px-4">Business</th>
              <th className="text-left py-3 px-4">Submitted</th>
              <th className="text-left py-3 px-4">Document</th>
              <th className="text-left py-3 px-4">Status</th>
              <th className="text-left py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-8 text-textTertiary">Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-8 text-textTertiary">No verifications.</td></tr>
            ) : (
              rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr className="border-b border-border/60 hover:bg-surface2/50 transition">
                    <td className="py-3 px-4 text-textPrimary">
                      {r.companyName || `Business #${r.userId}`}
                      <div className="text-textTertiary text-xs">{r.email}</div>
                    </td>
                    <td className="py-3 px-4 text-textTertiary">{new Date(r.submittedAt).toLocaleDateString()}</td>
                    <td className="py-3 px-4 text-textSecondary">
                      {r.documentType ? r.documentType.replace(/_/g, ' ') : '—'}
                      {r.documentUrl && (
                        <a
                          href={toAbsoluteUrl(r.documentUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-gold text-xs hover:underline"
                        >
                          View document
                        </a>
                      )}
                      {r.hasDiditSession && (
                        <button onClick={() => setDiditModalId(r.id)} className="block text-gold text-xs hover:underline text-left">
                          View Didit findings
                        </button>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor[r.status]}`}>{r.status.replace(/_/g, ' ')}</span>
                    </td>
                    <td className="py-3 px-4">
                      {isReviewable(r.status) && (
                        <button
                          onClick={() => setActingId(actingId === r.id ? null : r.id)}
                          className="text-xs px-3 py-1.5 rounded-lg bg-gold/10 border border-gold/30 text-gold hover:bg-gold/20 transition"
                        >
                          Review
                        </button>
                      )}
                    </td>
                  </tr>
                  {actingId === r.id && (
                    <tr className="border-b border-border/60 bg-surface2/30">
                      <td colSpan={5} className="py-4 px-4">
                        <div className="flex gap-3 items-start flex-wrap">
                          <textarea
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            placeholder="Rejection reason (required only if rejecting)"
                            rows={2}
                            className="flex-1 min-w-[200px] px-3 py-2 bg-surface1 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold resize-none"
                          />
                          <textarea
                            value={resubmissionNotes}
                            onChange={(e) => setResubmissionNotes(e.target.value)}
                            placeholder="Resubmission notes (required only if requesting changes)"
                            rows={2}
                            className="flex-1 min-w-[200px] px-3 py-2 bg-surface1 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold resize-none"
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => review(r.id, 'APPROVED')}
                              disabled={submitting}
                              className="text-sm px-4 py-2 rounded-lg bg-success/10 border border-success/30 text-success font-medium hover:bg-success/20 transition disabled:opacity-40"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => review(r.id, 'REJECTED')}
                              disabled={submitting}
                              className="text-sm px-4 py-2 rounded-lg bg-error/10 border border-error/30 text-error font-medium hover:bg-error/20 transition disabled:opacity-40"
                            >
                              Reject
                            </button>
                            <button
                              onClick={() => review(r.id, 'RESUBMISSION_REQUESTED')}
                              disabled={submitting}
                              className="text-sm px-4 py-2 rounded-lg bg-warning/10 border border-warning/30 text-warning font-medium hover:bg-warning/20 transition disabled:opacity-40"
                            >
                              Request Resubmission
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {diditModalId != null && <DiditFindingsModal verificationId={diditModalId} onClose={() => setDiditModalId(null)} />}
    </div>
  );
}

/** Proxies Didit's full KYB decision (documents, registry, AML, key-people) via our backend. */
function DiditFindingsModal({ verificationId, onClose }: { verificationId: number; onClose: () => void }) {
  const [decision, setDecision] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/business-finance/admin/verifications/${verificationId}/didit-decision`)
      .then((res) => setDecision(res.data))
      .catch(() => setError('Could not load Didit findings for this verification.'))
      .finally(() => setLoading(false));
  }, [verificationId]);

  // Best-effort extraction of document file URLs — the exact response shape
  // isn't fully documented publicly, so this degrades gracefully if absent.
  const documentUrls: string[] =
    decision?.document_verifications?.flatMap((d: any) => (d.items ?? []).map((i: any) => i.file_url).filter(Boolean)) ?? [];

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-surface1 border border-border rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-bold text-textPrimary">Didit KYB Findings</h2>
          <button onClick={onClose} className="text-textTertiary hover:text-textPrimary">✕</button>
        </div>
        {loading ? (
          <p className="text-textTertiary text-sm">Loading...</p>
        ) : error ? (
          <p className="text-error text-sm">{error}</p>
        ) : (
          <>
            {documentUrls.length > 0 && (
              <div className="mb-4">
                <p className="text-textSecondary text-xs mb-2">Documents</p>
                <div className="flex flex-col gap-1">
                  {documentUrls.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="text-gold text-sm hover:underline">
                      Document {i + 1}
                    </a>
                  ))}
                </div>
              </div>
            )}
            <p className="text-textSecondary text-xs mb-2">Full decision payload</p>
            <pre className="bg-surface2 border border-border rounded-lg p-3 text-xs text-textPrimary overflow-x-auto whitespace-pre-wrap break-words">
              {JSON.stringify(decision, null, 2)}
            </pre>
          </>
        )}
      </div>
    </div>
  );
}
